/**
 * Edit one Engagement Standard Tier — Settings ▸ Tiers.
 *
 * One dialog, no create/delete twin: the four tier names are pinned by a database CHECK
 * and by string-union types across the app, and four RESTRICT foreign keys mean a tier in
 * use cannot be removed. Tier identity is a schema fact, so this dialog edits
 * configuration only.
 *
 * TWO WRITES, TWO CAPABILITIES. The rate card goes to
 * PUT /api/engagement-standard-tiers/:id/rate-basis (`manageRateCard` — [FIN]'s artefact);
 * everything else to PATCH /api/engagement-standard-tiers/:id (`manageConfiguration` —
 * ADMIN/RM). A signed-in user may hold one and not the other, so each group of fields is
 * read-only on its own, and the submit only fires the calls whose values actually moved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type TierConfigView, useUpdateTier } from "@/hooks/use-engagement-standard-tiers";
import type { PodShape } from "@/hooks/use-pod-shapes";
import { type RateBasisView, useSetTierRateBasis } from "@/hooks/use-rate-card";

/** Radix SelectItem forbids an empty-string value — this sentinel maps back to null. */
const NO_SHAPE = "__none__";

/**
 * A whole number ≥ 0, as typed. Kept as a string in the form so an emptied field is an
 * empty box the user can retype into rather than a 0 that silently saves.
 */
const wholeNumber = (label: string) =>
  z
    .string()
    .trim()
    .regex(/^\d+$/, { message: `${label} must be a whole number, 0 or more` });

const schema = z.object({
  description: z.string().trim().max(500),
  rateBasisId: z.string().min(1, "Pick a rate card"),
  defaultShapeId: z.string(),
  defaultR1Points: wholeNumber("R1 points"),
  defaultR2Points: wholeNumber("R2 points"),
  defaultR3Points: wholeNumber("R3 points"),
  /** §3.5.4: only Strategic populates R4 today, so blank is a legitimate value. */
  defaultR4Points: z
    .string()
    .trim()
    .regex(/^\d*$/, { message: "R4 points must be a whole number, or blank" }),
  minimumExpectationLevel: z.string(),
  ceilingPenaltyScalingFactor: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, { message: "Use a number with at most 2 decimal places" })
    .refine((v) => Number(v) <= 99.99, { message: "Must be 99.99 or less" }),
  expectationPenaltyStep: z
    .string()
    .trim()
    .regex(/^\d+$/, { message: "The penalty step must be a whole number" })
    .refine((v) => Number(v) > 0, { message: "The penalty step must be greater than 0" }),
});

type FormValues = z.infer<typeof schema>;

function valuesFor(tier: TierConfigView): FormValues {
  return {
    description: tier.description ?? "",
    rateBasisId: tier.rateBasisId,
    defaultShapeId: tier.defaultShapeId ?? NO_SHAPE,
    defaultR1Points: String(tier.defaultR1Points),
    defaultR2Points: String(tier.defaultR2Points),
    defaultR3Points: String(tier.defaultR3Points),
    defaultR4Points: tier.defaultR4Points === null ? "" : String(tier.defaultR4Points),
    minimumExpectationLevel: String(tier.minimumExpectationLevel),
    ceilingPenaltyScalingFactor: tier.ceilingPenaltyScalingFactor,
    expectationPenaltyStep: String(tier.expectationPenaltyStep),
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: TierConfigView;
  bases: RateBasisView[];
  shapes: PodShape[];
  /** `manageRateCard` — may edit the tier's rate card. */
  canEditRateCard: boolean;
  /** `manageConfiguration` — may edit everything else. */
  canEditDefaults: boolean;
}

export function TierEditDialog({
  open,
  onOpenChange,
  tier,
  bases,
  shapes,
  canEditRateCard,
  canEditDefaults,
}: Props) {
  const update = useUpdateTier();
  const setBasis = useSetTierRateBasis();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: valuesFor(tier),
  });

  useEffect(() => {
    if (!open) return;
    form.reset(valuesFor(tier));
  }, [open, tier, form]);

  async function onSubmit(values: FormValues) {
    // The rate card is a separate endpoint under a separate capability, so it is sent
    // only when it actually moved — otherwise a defaults-only edit by an RM would 403.
    if (canEditRateCard && values.rateBasisId !== tier.rateBasisId) {
      await setBasis.mutateAsync({ tierId: tier.id, rateBasisId: values.rateBasisId });
    }
    if (canEditDefaults) {
      await update.mutateAsync({
        tierId: tier.id,
        description: values.description.trim() ? values.description.trim() : null,
        defaultShapeId: values.defaultShapeId === NO_SHAPE ? null : values.defaultShapeId,
        defaultR1Points: Number(values.defaultR1Points),
        defaultR2Points: Number(values.defaultR2Points),
        defaultR3Points: Number(values.defaultR3Points),
        defaultR4Points: values.defaultR4Points === "" ? null : Number(values.defaultR4Points),
        minimumExpectationLevel: Number(values.minimumExpectationLevel),
        ceilingPenaltyScalingFactor: values.ceilingPenaltyScalingFactor,
        expectationPenaltyStep: Number(values.expectationPenaltyStep),
      });
    }
    onOpenChange(false);
  }

  const pending = update.isPending || setBasis.isPending;
  const activeShapes = shapes.filter((s) => s.active || s.id === tier.defaultShapeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {tier.name} tier</DialogTitle>
          <DialogDescription>
            The tier's name is fixed — Accounts, Solutions and Plans reference it. Everything below
            is configuration and takes effect on the next Solution priced or scored under this tier.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          {/* handleSubmit swallows the rejection an async submit can throw; the mutations
              surface their own error toasts, so the dialog simply stays open. */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder="Optional"
                      disabled={!canEditDefaults}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rateBasisId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate card</FormLabel>
                  {/* One tier, one rate card: a single-select is what makes that true at
                      the interface, and the column is NOT NULL underneath it. */}
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!canEditRateCard}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {bases
                        .filter((b) => b.active || b.id === tier.rateBasisId)
                        .map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                            {b.complete ? "" : " — no rates"}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {canEditRateCard
                      ? "Every Solution on this tier is priced against this card. There is no fallback — a card with no rates blocks plan creation."
                      : "Only Finance can change which card a tier prices on."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultShapeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Shape</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!canEditDefaults}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_SHAPE}>
                        None — fall back to the default Pod size
                      </SelectItem>
                      {activeShapes.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} · {s.composition}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Applied to new Solutions on this tier. Existing Solutions keep the Shape they
                    were staffed with.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {(
                [
                  ["defaultR1Points", "R1 points"],
                  ["defaultR2Points", "R2 points"],
                  ["defaultR3Points", "R3 points"],
                  ["defaultR4Points", "R4 points"],
                ] as const
              ).map(([name, label]) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{label}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          placeholder={name === "defaultR4Points" ? "—" : undefined}
                          disabled={!canEditDefaults}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Points, not hours — the default load each Pod position carries on this tier. Leave R4
              blank on a tier that does not staff a fourth position.
            </p>

            <FormField
              control={form.control}
              name="minimumExpectationLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Expectation Level</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!canEditDefaults}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <SelectItem key={level} value={String(level)}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The floor an Account on this tier cannot be set below.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="expectationPenaltyStep"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expectation Match penalty step</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" disabled={!canEditDefaults} {...field} />
                    </FormControl>
                    <FormDescription>
                      How steeply a rating that misses the Expectation Level is penalised. Default
                      25.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ceilingPenaltyScalingFactor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ceiling penalty scaling factor</FormLabel>
                    <FormControl>
                      <Input inputMode="decimal" disabled={!canEditDefaults} {...field} />
                    </FormControl>
                    <FormDescription>
                      How much over-qualification counts against the Fit Score.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || (!canEditDefaults && !canEditRateCard)}>
                {pending ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
